/**
 * Passkeys in the browser (ADR-024 §10) — thin wrappers over
 * @simplewebauthn/browser so every screen speaks the same words when the
 * browser says no.
 *
 * The server makes the options and checks the answers; nothing here is
 * trusted. A passkey works only on the site that made it — fias.io's on
 * fias.io, a Company portal's on that portal — and the browser enforces that.
 */
import {
  WebAuthnError,
  browserSupportsWebAuthn,
  startAuthentication,
  startRegistration,
} from '@simplewebauthn/browser';
import type {
  AuthenticationResponseJSON,
  PublicKeyCredentialCreationOptionsJSON,
  PublicKeyCredentialRequestOptionsJSON,
  RegistrationResponseJSON,
} from '@simplewebauthn/browser';

export type {
  AuthenticationResponseJSON,
  PublicKeyCredentialCreationOptionsJSON,
  PublicKeyCredentialRequestOptionsJSON,
  RegistrationResponseJSON,
};

/** False on a browser with no WebAuthn at all (very old, or locked down). */
export function passkeysAvailable(): boolean {
  try {
    return browserSupportsWebAuthn();
  } catch {
    return false;
  }
}

/** Makes a passkey from the server's options. Throws {@link PasskeyCancelled} or an Error. */
export async function createPasskeyCredential(
  options: PublicKeyCredentialCreationOptionsJSON,
): Promise<RegistrationResponseJSON> {
  try {
    return await startRegistration({ optionsJSON: options });
  } catch (err) {
    throw toPasskeyError(err, 'create');
  }
}

/** Signs the server's challenge with a passkey. Throws {@link PasskeyCancelled} or an Error. */
export async function getPasskeyAssertion(
  options: PublicKeyCredentialRequestOptionsJSON,
): Promise<AuthenticationResponseJSON> {
  try {
    return await startAuthentication({ optionsJSON: options });
  } catch (err) {
    throw toPasskeyError(err, 'use');
  }
}

/**
 * The person closed the browser's passkey prompt, or it timed out. Not an
 * error worth a red banner — a quiet "try again".
 */
export class PasskeyCancelled extends Error {
  constructor() {
    super('The passkey prompt was closed before it finished.');
    this.name = 'PasskeyCancelled';
  }
}

function toPasskeyError(err: unknown, action: 'create' | 'use'): Error {
  const name = (err as { name?: string })?.name;
  if (err instanceof WebAuthnError && err.code === 'ERROR_CEREMONY_ABORTED') {
    return new PasskeyCancelled();
  }
  // NotAllowedError: the person cancelled, the prompt timed out, or (for a
  // sign-in) there was no passkey for this site to offer.
  if (name === 'NotAllowedError') return new PasskeyCancelled();
  if (err instanceof WebAuthnError) {
    switch (err.code) {
      case 'ERROR_AUTHENTICATOR_PREVIOUSLY_REGISTERED':
        return new Error('This device already has a passkey for this site.');
      case 'ERROR_AUTHENTICATOR_MISSING_USER_VERIFICATION_SUPPORT':
      case 'ERROR_AUTHENTICATOR_MISSING_DISCOVERABLE_CREDENTIAL_SUPPORT':
        return new Error(
          'This device cannot make a passkey that unlocks with a fingerprint, face or PIN. Try your phone, or another device.',
        );
      default:
        break;
    }
  }
  return new Error(
    action === 'create'
      ? 'Your device could not make a passkey. Please try again.'
      : 'Your device could not use a passkey. Please try again.',
  );
}
